import React, { useState, useEffect, useCallback } from "react";
import {
  Typography, Box, Button, MenuItem, Select, FormControl,
  InputLabel, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Paper, CircularProgress, Alert, Chip, TablePagination,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import api from "../../api/api";

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

const PayStubGenerator = ({ year, month }) => {
  const [staffList, setStaffList] = useState([]);
  const [stubs, setStubs] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState("");
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 5;

  const fetchStubs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [staffRes, stubsRes] = await Promise.all([
        api.get("/staff/"),
        api.get("/invoicing/pay-stubs/"),
      ]);
      setStaffList(staffRes.data);
      // filter to current month/year
      const filtered = stubsRes.data.filter((s) => {
        const d = new Date(s.month);
        return d.getFullYear() === year && d.getMonth() + 1 === month;
      });
      setStubs(filtered);
    } catch (e) {
      setError("Failed to load data.");
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { fetchStubs(); }, [fetchStubs]);

  const handleGenerate = async () => {
    if (!selectedStaff) return;
    setGenerating(true);
    setError(null);
    try {
      await api.post("/invoicing/pay-stubs/generate/", {
        staff_id: selectedStaff,
        year,
        month,
      });
      await fetchStubs();
    } catch (e) {
      setError("Generation failed: " + (e.response?.data ? JSON.stringify(e.response.data) : e.message));
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadPdf = async (stubId, filename) => {
    setDownloading((d) => ({ ...d, [stubId]: true }));
    try {
      const baseURL = process.env.REACT_APP_API_URL || "http://localhost:8000";
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${baseURL}/api/invoicing/pay-stubs/${stubId}/pdf/`,
        { headers: { Authorization: `Token ${token}` } }
      );
      if (!response.ok) throw new Error("PDF download failed");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || `paystub_${stubId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError("PDF download failed: " + e.message);
    } finally {
      setDownloading((d) => ({ ...d, [stubId]: false }));
    }
  };

  const staffName = (stub) => {
    const s = staffList.find((x) => x.id === stub.staff);
    if (!s) return `Staff #${stub.staff}`;
    const d = s.user_details;
    return d?.first_name ? `${d.first_name} ${d.last_name}` : d?.username ?? `#${stub.staff}`;
  };

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Generate section */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Generate pay stub for <strong>{MONTHS[month - 1]} {year}</strong>
        </Typography>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Select Employee</InputLabel>
            <Select
              value={selectedStaff}
              label="Select Employee"
              onChange={(e) => setSelectedStaff(e.target.value)}
            >
              {staffList.map((s) => {
                const d = s.user_details;
                const name = d?.first_name ? `${d.first_name} ${d.last_name}` : d?.username ?? `#${s.id}`;
                return (
                  <MenuItem key={s.id} value={s.id}>
                    {name}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <AutorenewIcon />}
            onClick={handleGenerate}
            disabled={!selectedStaff || generating}
          >
            {generating ? "Generating…" : "Generate / Refresh"}
          </Button>
        </Box>
      </Paper>

      {/* Stubs table */}
      <Typography variant="subtitle1" gutterBottom>
        Pay stubs for {MONTHS[month - 1]} {year} ({stubs.length})
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#2c5f8a" }}>
              {["Employee", "Month", "Sessions", "Total Hours", "Total Gross", "Generated", "Download"].map((h) => (
                <TableCell key={h} sx={{ color: "white", fontWeight: "bold", py: 1 }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {stubs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No pay stubs yet. Select an employee and click Generate.
                </TableCell>
              </TableRow>
            ) : stubs.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map((stub, i) => (
              <TableRow key={stub.id} sx={{ "&:nth-of-type(even)": { backgroundColor: "#f0f4f8" } }}>
                <TableCell>{staffName(stub)}</TableCell>
                <TableCell>{stub.month_display}</TableCell>
                <TableCell>
                  <Chip label={stub.sessions?.length ?? 0} size="small" color="primary" />
                </TableCell>
                <TableCell align="right">{stub.total_hours?.toFixed(2)}</TableCell>
                <TableCell align="right"><strong>€ {stub.total_gross?.toFixed(2)}</strong></TableCell>
                <TableCell>{new Date(stub.generated_at).toLocaleString()}</TableCell>
                <TableCell>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={downloading[stub.id] ? <CircularProgress size={14} /> : <DownloadIcon />}
                    onClick={() => handleDownloadPdf(stub.id)}
                    disabled={!!downloading[stub.id]}
                  >
                    PDF
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={stubs.length}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={PAGE_SIZE}
        rowsPerPageOptions={[5]}
      />
    </Box>
  );
};

export default PayStubGenerator;
